// src/components/admin/CreateTransportPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import { AdminShell } from './Sidebar';
import { TransportProfilePageRoute } from '../../consts';
import { LoadingState } from '../ui/CrmPage';

export default function CreateTransportPage() {
  const navigate = useNavigate();
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [form, setForm] = useState({
    name: '', registration_number: '', document: '',
    id_organization: '', id_division: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [orgs, setOrgs] = useState([]);
  const [divs, setDivs] = useState([]);
  const [refsLoading, setRefsLoading] = useState(true);
  const [refsError, setRefsError] = useState('');

  useEffect(() => {
    (async () => {
      setRefsLoading(true); setRefsError('');
      try {
        const [orgRes, divRes] = await Promise.all([
          fetch(`${apiBase}v1/organizations`),
          fetch(`${apiBase}v1/divisions`),
        ]);
        if (!orgRes.ok) throw new Error(`Организации: HTTP ${orgRes.status}`);
        if (!divRes.ok) throw new Error(`Подразделения: HTTP ${divRes.status}`);
        setOrgs(await orgRes.json());
        setDivs(await divRes.json());
      } catch (e) {
        setRefsError(e.message || 'Не удалось загрузить справочники');
      } finally {
        setRefsLoading(false);
      }
    })();
  }, [apiBase]);

  const onChange = (e) => setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaveError(''); setSaving(true);
    try {
      if (!form.name.trim()) throw new Error('Укажите название');
      if (!form.registration_number.trim()) throw new Error('Укажите гос. номер');
      if (!form.document.trim()) throw new Error('Укажите документ');
      if (!String(form.id_organization)) throw new Error('Выберите организацию');
      if (!String(form.id_division)) throw new Error('Выберите подразделение');
      const body = {
        name: form.name.trim(),
        registration_number: form.registration_number.trim(),
        document: form.document.trim(),
        id_organization: Number(form.id_organization),
        id_division: Number(form.id_division),
      };
      const r = await fetch(`${apiBase}v1/vehicles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status} ${r.statusText} ${t}`.trim());
      }
      const created = await r.json();
      const newId = String(created.id ?? '');
      if (newId) navigate(generatePath(TransportProfilePageRoute, { id: newId }));
      else setForm({ name: '', registration_number: '', document: '', id_organization: '', id_division: '' });
    } catch (e) {
      setSaveError(e.message || 'Не удалось создать ТС');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell
      breadcrumbs="Транспорт"
      title="Добавить ТС"
      subtitle="Заполните обязательные поля. После создания откроется карточка для уточнения."
      actions={
        <>
          <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Отмена</button>
          <button
            type="submit"
            form="create-transport-form"
            className="adminbtn adminbtn--primary"
            disabled={saving || refsLoading}
          >
            {saving ? 'Сохранение…' : 'Создать ТС'}
          </button>
        </>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          {!!refsError && <div className="crmpage__alert crmpage__alert--warning">{refsError}</div>}
          {!!saveError && <div className="crmpage__alert crmpage__alert--error">{saveError}</div>}

          {refsLoading ? (
            <LoadingState text="Загружаем справочники…" />
          ) : (
            <form id="create-transport-form" className="adminform" onSubmit={onSubmit} style={{ maxWidth: 720 }}>
              <section className="adminform__card">
                <div className="adminform__section-title">Новое транспортное средство</div>
                <div className="adminform__grid">
                  <label className="adminform__field adminform__field--full">
                    <span className="adminform__label">Название<span className="req">*</span></span>
                    <input className="adminform__input" name="name" value={form.name} onChange={onChange} required />
                  </label>
                  <label className="adminform__field">
                    <span className="adminform__label">Гос. номер<span className="req">*</span></span>
                    <input className="adminform__input" name="registration_number" value={form.registration_number} onChange={onChange} required />
                  </label>
                  <label className="adminform__field">
                    <span className="adminform__label">Документ<span className="req">*</span></span>
                    <input className="adminform__input" name="document" value={form.document} onChange={onChange} required />
                  </label>
                  <label className="adminform__field">
                    <span className="adminform__label">Организация<span className="req">*</span></span>
                    <select className="adminform__select" name="id_organization" value={String(form.id_organization)} onChange={onChange} required>
                      <option value="">— выберите —</option>
                      {orgs.map((o) => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
                    </select>
                  </label>
                  <label className="adminform__field">
                    <span className="adminform__label">Подразделение<span className="req">*</span></span>
                    <select className="adminform__select" name="id_division" value={String(form.id_division)} onChange={onChange} required>
                      <option value="">— выберите —</option>
                      {divs.map((d) => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
                    </select>
                  </label>
                </div>
              </section>
            </form>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
